import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import * as api from "../../lib/claimsApi.ts";
import type { VerifyResponse } from "../../types/claims";
import { useLocationState } from "../../hooks/useLocationState";
import { ApiError, genericApiError, isSentryWorthy } from "../../lib/errors";
import { logMetricEvent } from "../../lib/delivery";
import { MetricEvents } from "@shared/integrations/delivery_keys";
import { useOnceWhen } from "../../hooks/useOnceWhen";
import { usePageView } from "../../hooks/usePageView";

interface UseVerifyResultResult {
  result: VerifyResponse | null;
  isLoading: boolean;
  apiError: ApiError | null;
  /** The route's :userEventVerificationId param — always present, regardless of whether
   *  `result` hydrated from router state or a server fetch. */
  userEventVerificationId: string | undefined;
}

/**
 * Reads the verification result for the current route.
 *
 * Hydration order:
 * 1. React Router `location.state.result` — set by the Analysing page after a fresh API call.
 * 2. Server-side fetch by user_event_verification_id — enables shared links on any device.
 */
export function useVerifyResult(): UseVerifyResultResult {
  const { userEventVerificationId } = useParams<{ userEventVerificationId: string }>();
  const stateResult = useLocationState<VerifyResponse>("result");
  const [apiResult, setApiResult] = useState<VerifyResponse | null>(null);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(!stateResult && !!userEventVerificationId);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (stateResult) return;
    if (!userEventVerificationId || hasFetched.current) return;
    hasFetched.current = true;

    api
      .getResult(userEventVerificationId)
      .then((result) => {
        if (result) setApiResult(result);
      })
      .catch((err) => {
        const error = err instanceof ApiError ? err : genericApiError(err);
        setApiError(error);
        if (isSentryWorthy(err)) {
          Sentry.captureException(err, {
            tags: { "error.code": error.code },
            extra: { endpoint: "/verifications/:id", userEventVerificationId },
          });
        }
      })
      .finally(() => setIsLoading(false));
  }, [stateResult, userEventVerificationId]);

  const result = stateResult ?? apiResult;

  usePageView("verify");
  useOnceWhen(result !== null, () => {
    logMetricEvent(MetricEvents.platformVerificationViewed, {
      metadata: { verdict: result!.claim_verdict.verdict },
    });
  });

  return {
    result,
    isLoading,
    apiError,
    userEventVerificationId,
  };
}
